import utils from "./utils.js";

// Touch / pinch-zoom / swipe / mobile inner-zoom.

const touchMethods = {
    _touchStart(e) {
        if (this.items[this.index]?.type === "video") return;
        // Skip touch-zoom when a linked video is playing — taps must reach
        // mainVideo so the click-to-play/pause handler can fire.
        if (this.mainWrap && this.mainWrap.classList.contains("wcpg-linked-video-active")) return;
        const zs = this._zs;

        // Show auto-hide overlay controls on touch — mirrors desktop :hover behaviour.
        // Add class to mainWrap (arrows, lb-trigger) and stage (pagination).
        this.mainWrap.classList.add("wcpg-touch-active");
        if (this.stage) this.stage.classList.add("wcpg-touch-active");
        if (this._touchActiveTimer) clearTimeout(this._touchActiveTimer);
        this._touchActiveTimer = setTimeout(() => {
            this.mainWrap.classList.remove("wcpg-touch-active");
            if (this.stage)
                this.stage.classList.remove("wcpg-touch-active");
        }, 3000);

        // If the touch target is an overlay control (arrow, dot, lb-trigger,
        // linked-video play button), skip all zoom logic and let the element's
        // click event handle it.
        if (
            e.target.closest(
                ".wcpg-main-arrow, .wcpg-pagination, .wcpg-lb-trigger, .wcpg-linked-video-btn",
            )
        )
            return;

        // Cancel any running momentum
        if (zs.momentumRaf) {
            cancelAnimationFrame(zs.momentumRaf);
            zs.momentumRaf = null;
        }

        if (e.touches.length === 2) {
            // End mobile inner zoom if active — switching to pinch
            if (zs.mobileZooming) {
                zs.mobileZooming = false;
                this.mainImg.style.transition = "";
                this.mainImg.style.transformOrigin = "";
                this.mainImg.style.transform = "";
            }
            e.preventDefault();
            zs.pinching = true;
            const t0 = e.touches[0],
                t1 = e.touches[1];
            zs.dist0 = Math.hypot(
                t0.clientX - t1.clientX,
                t0.clientY - t1.clientY,
            );
            zs.scale0 = zs.scale;

            // Pinch midpoint → becomes stable transform origin
            const rect = this.mainWrap.getBoundingClientRect();
            zs.originX =
                (t0.clientX + t1.clientX) / 2 - rect.left - rect.width / 2;
            zs.originY =
                (t0.clientY + t1.clientY) / 2 - rect.top - rect.height / 2;
        } else if (e.touches.length === 1) {
            const t = e.touches[0];
            const now = Date.now();

            // Double-tap: toggle pinch-zoom in/out
            const dtap = now - zs.lastTapT;
            if (dtap > 0 && dtap < 300) {
                e.preventDefault();
                // End any mobile inner zoom first
                if (zs.mobileZooming) {
                    zs.mobileZooming = false;
                    this.mainImg.style.transformOrigin = "";
                }
                if (zs.scale > 1) {
                    this._touchResetZoom();
                } else {
                    const rect = this.mainWrap.getBoundingClientRect();
                    const tapX = t.clientX - rect.left - rect.width / 2;
                    const tapY = t.clientY - rect.top - rect.height / 2;
                    zs.originX = tapX;
                    zs.originY = tapY;
                    const target = utils.clamp(
                        2.5,
                        this.opts.touchZoomMin,
                        this.opts.touchZoomMax,
                    );
                    const [cx, cy] = this._clampPan(
                        tapX * (1 - target),
                        tapY * (1 - target),
                        target,
                    );
                    zs.scale = target;
                    zs.panX = cx;
                    zs.panY = cy;
                    this.mainImg.style.transition = "transform 0.25s ease";
                    this._applyTouchTransform();
                    setTimeout(() => {
                        this.mainImg.style.transition = "";
                    }, 260);
                }
                zs.lastTapT = 0;
                return;
            }
            zs.lastTapT = now;

            if (zs.scale > 1) {
                // Already pinch-zoomed in — pan mode
                zs.panX0 = t.clientX - zs.panX;
                zs.panY0 = t.clientY - zs.panY;
                zs.lastX = t.clientX;
                zs.lastY = t.clientY;
                zs.lastT = now;
                zs.velX = 0;
                zs.velY = 0;
            } else if (this._isMobile) {
                // Mobile inner zoom: image zooms in at touch point and follows finger
                e.preventDefault();
                zs.mobileZooming = true;
                this._suppressClick = false;
                // Swap to full-resolution zoom image for a sharp zoomed view
                const item = this.items[this.index];
                if (
                    item &&
                    item.zoomSrc &&
                    item.zoomSrc !== this.mainImg.src
                ) {
                    this.mainImg.src = item.zoomSrc;
                }
                this._applyMobileInnerZoom(t.clientX, t.clientY);
            }
        }
    },

    _touchMove(e) {
        if (this.items[this.index]?.type === "video") return;
        const zs = this._zs;

        if (e.touches.length === 2 && zs.pinching) {
            e.preventDefault();
            const t0 = e.touches[0],
                t1 = e.touches[1];
            const d = Math.hypot(
                t0.clientX - t1.clientX,
                t0.clientY - t1.clientY,
            );
            const newScale = utils.clamp(
                zs.scale0 * (d / zs.dist0),
                this.opts.touchZoomMin,
                this.opts.touchZoomMax,
            );

            // Adjust pan so the pinch midpoint stays anchored
            const scaleDelta = newScale / zs.scale;
            zs.panX = zs.originX + (zs.panX - zs.originX) * scaleDelta;
            zs.panY = zs.originY + (zs.panY - zs.originY) * scaleDelta;
            zs.scale = newScale;
            const [cx, cy] = this._clampPan(zs.panX, zs.panY, zs.scale);
            zs.panX = cx;
            zs.panY = cy;
            this._applyTouchTransform();
        } else if (e.touches.length === 1) {
            if (zs.mobileZooming) {
                // Mobile inner zoom: follow finger
                e.preventDefault();
                this._suppressClick = true;
                this._applyMobileInnerZoom(
                    e.touches[0].clientX,
                    e.touches[0].clientY,
                );
            } else if (zs.scale > 1) {
                // Pinch-zoom pan with momentum tracking
                e.preventDefault();
                const t = e.touches[0];
                const now = Date.now();
                const dt = Math.max(1, now - zs.lastT);
                const rawX = t.clientX - zs.panX0;
                const rawY = t.clientY - zs.panY0;
                const [cx, cy] = this._clampPan(rawX, rawY, zs.scale);
                zs.velX = ((cx - zs.panX) / dt) * 16;
                zs.velY = ((cy - zs.panY) / dt) * 16;
                zs.lastX = t.clientX;
                zs.lastY = t.clientY;
                zs.lastT = now;
                zs.panX = cx;
                zs.panY = cy;
                this._applyTouchTransform();
            }
        }
    },

    _touchEnd(e) {
        const zs = this._zs;
        zs.pinching = false;

        // End mobile inner zoom — animate back to normal
        if (zs.mobileZooming) {
            zs.mobileZooming = false;
            const item = this.items[this.index];
            this.mainImg.style.transition = "transform 0.25s ease";
            this.mainImg.style.transform = "";
            setTimeout(() => {
                this.mainImg.style.transition = "";
                this.mainImg.style.transformOrigin = "";
                // Restore display-resolution image now that zoom is hidden
                if (item && item.src && this.mainImg.src !== item.src) {
                    this.mainImg.src = item.src;
                }
                // Suppress the synthetic click that follows touchend
                setTimeout(() => {
                    this._suppressClick = false;
                }, 50);
            }, 260);
            return;
        }

        if (zs.scale <= 1.05) {
            this._touchResetZoom();
            return;
        }

        // Momentum: continue sliding and decelerate after fast pan
        const decay = 0.92;
        const step = () => {
            if (Math.abs(zs.velX) < 0.3 && Math.abs(zs.velY) < 0.3) {
                zs.momentumRaf = null;
                return;
            }
            zs.velX *= decay;
            zs.velY *= decay;
            const [cx, cy] = this._clampPan(
                zs.panX + zs.velX,
                zs.panY + zs.velY,
                zs.scale,
            );
            if (cx !== zs.panX + zs.velX) zs.velX = 0;
            if (cy !== zs.panY + zs.velY) zs.velY = 0;
            zs.panX = cx;
            zs.panY = cy;
            this._applyTouchTransform();
            zs.momentumRaf = requestAnimationFrame(step);
        };
        this.mainImg.style.transition = "none";
        zs.momentumRaf = requestAnimationFrame(step);
    },

    _touchResetZoom() {
        const zs = this._zs;
        zs.scale = 1;
        zs.panX = 0;
        zs.panY = 0;
        zs.originX = 0;
        zs.originY = 0;
        this.mainImg.style.transition = "transform 0.28s ease";
        this._applyTouchTransform();
        setTimeout(() => {
            this.mainImg.style.transition = "";
        }, 290);
    },

    // Clamp pan so the scaled image never shows empty space around it.
    _clampPan(panX, panY, scale) {
        const wrap = this.mainWrap;
        const img = this.mainImg;
        const ww = wrap.offsetWidth;
        const wh = wrap.offsetHeight;
        const iw = img.offsetWidth * scale;
        const ih = img.offsetHeight * scale;
        const maxX = Math.max(0, (iw - ww) / 2);
        const maxY = Math.max(0, (ih - wh) / 2);
        return [
            utils.clamp(panX, -maxX, maxX),
            utils.clamp(panY, -maxY, maxY),
        ];
    },

    _applyTouchTransform() {
        const zs = this._zs;
        this.mainImg.style.transform = `translate(${zs.panX}px, ${zs.panY}px) scale(${zs.scale})`;
    },

    // Inner zoom that follows the finger — used on mobile for all zoom types.
    _applyMobileInnerZoom(clientX, clientY) {
        const rect = this.mainWrap.getBoundingClientRect();
        const pctX = utils.clamp((clientX - rect.left) / rect.width, 0, 1);
        const pctY = utils.clamp((clientY - rect.top) / rect.height, 0, 1);
        const z = this.opts.zoomLevel;
        this.mainImg.style.transition = "none";
        this.mainImg.style.transformOrigin = `${pctX * 100}% ${
            pctY * 100
        }%`;
        this.mainImg.style.transform = `scale(${z})`;
    },

    // Keeps _isMobile in sync with the viewport as the browser is resized.
    _initMobileObserver() {
        const mq = window.matchMedia("(max-width: 768px)");
        this._mobileMq = mq;
        this._mobileMqFn = (e) => {
            const wasMobile = this._isMobile;
            this._isMobile = e.matches;
            // Switched to desktop while mobile inner zoom was active — clean up
            if (wasMobile && !this._isMobile && this._zs.mobileZooming) {
                this._zs.mobileZooming = false;
                this.mainImg.style.transition = "";
                this.mainImg.style.transformOrigin = "";
                this.mainImg.style.transform = "";
                this._suppressClick = false;
            }
            // Update zoom-in cursor for lightboxOpenOnImageClick
            if (
                this.mainWrap &&
                this.opts.lightbox &&
                this.opts.lightboxOpenOnImageClick
            ) {
                this.mainWrap.style.cursor = this._isMobile
                    ? ""
                    : "zoom-in";
            }
        };
        mq.addEventListener("change", this._mobileMqFn);
    },
};

export default touchMethods;
